import type { Asset, Entry } from 'contentful';
import { isValidElement, type ReactNode } from 'react';

import { ErrorBoundary } from '@libs/common-next';

import type { PropsType } from '../types';
import { ComponentResolver } from './ComponentResolver';
import { componentMapping } from './component-mapping';
import { createUniqueIDs } from './createUniqueIDs';

const doesComponentExist = (child: Entry | Asset): boolean => {
    if (child?.sys?.type === 'Asset') {
        return false;
    }

    const typeName = child?.sys?.contentType?.sys?.id;

    return !!componentMapping[typeName];
};

const filterChild = (child: ReactNode | PropsType) => {
    if (child !== null) {
        return typeof child === 'object' && !isValidElement(child) ? Object.values(child).every(v => v !== null) : true;
    }
    return false;
};

type BuildEntry = (component: Entry, componentKey?: string | number) => ReactNode | PropsType;

export function buildComponent(
    component: Entry,
    componentKey?: string | number,
    resolveChild: BuildEntry = buildComponent
): ReactNode | PropsType {
    if (!component?.fields) {
        return null;
    }

    const entries = Object.entries(component.fields).map(([key, value]) => {
        if (Array.isArray(value) && value[0]?.sys !== undefined) {
            const childrenComponents: Entry[] = createUniqueIDs(value);
            const branch = childrenComponents.map((child, index) => resolveChild(child, index)).filter(filterChild);

            return [key, branch];
        }

        if (value?.sys !== undefined) {
            return [key, resolveChild(value)];
        }

        return [key, value];
    });

    const props = {
        ...component,
        fields: Object.fromEntries(entries),
    };
    return doesComponentExist(props) ? (
        <ErrorBoundary key={componentKey} tags={{ contentful: 'true' }}>
            <ComponentResolver entry={props} />
        </ErrorBoundary>
    ) : (
        props.fields
    );
}
